/// <reference types="@types/google.maps" />
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Geolocation } from '@capacitor/geolocation';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { Network } from '@capacitor/network';
import { PushNotifications } from '@capacitor/push-notifications';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { IonApp, Platform } from '@ionic/angular';
import { IonicStorageModule } from '@ionic/storage-angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { VonToastComponent } from '@von-development-studio/primeng-helper';
import config from 'devextreme/core/config';
import { loadMessages, locale } from 'devextreme/localization';
import * as enMessages from 'devextreme/localization/messages/en.json';
import * as esMessages from 'devextreme/localization/messages/es.json';
import { CookieService } from 'ngx-cookie-service';
import { PrimeNG } from 'primeng/config';
import { finalize, interval, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppService } from './app.service';
import { FirebaseService } from './core/services/firebase.service';
import { AppStorage } from './core/utils/app.storage';
import { AppStorageService } from './core/utils/app.storage.service';
import { AiChatInformation } from './models/by-name/ai-chat-information';
import { SecurityUser } from './models/security/security-user';
import { appStoreGlobalActions } from './store/global/store.global.action';
import { appStoreScreenActions } from './store/screen/store.screen.action';
import { appStoreSettingsActions } from './store/settings/store.settings.action';

import primeNgEn from '@a/i18n/primeng/en.json';
import primeNgEs from '@a/i18n/primeng/es.json';

const primeNgLanguage: {
  [key: string]: typeof primeNgEn | typeof primeNgEs;
} = {
  en: primeNgEn,
  es: primeNgEs,
};

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
  imports: [
    IonApp,
    RouterOutlet,

    // VON Libraries
    VonToastComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [IonicStorageModule],
})
export class AppComponent implements OnInit {
  isWeb = false;
  isSmall = false;

  sessionInStorage = '';
  agentDocument = '';

  subscriptions: { [key: string]: Subscription } = {};

  private user?: SecurityUser;

  private geolocationPositionId?: string;

  constructor(
    protected primeNg: PrimeNG,
    protected storage: AppStorageService,
    protected translate: TranslateService,
    protected platform: Platform,
    protected store: Store<any>,
    protected service: AppService,
    protected cookie: CookieService,
    protected firebaseService: FirebaseService,
  ) {}

  ngOnInit(): void {
    this.platform.ready().then(async () => {
      if (!this.platform.is('mobileweb') && !this.platform.is('desktop')) {
        Keyboard.setResizeMode({ mode: KeyboardResize.None }).then();
      }

      this.firebaseService.listeners();
      this.firebaseService.permission();

      this.store.select('global', 'user', 'username').subscribe({
        next: () => {
          this.checkCurrentSession();
        },
      });

      this.setLocalizationForPrimeNG();

      this.translate.get('GLOBAL_DEVEXTREME').subscribe(this.setLocalizationForDevextreme);

      this.checkCurrentSession();

      this.storage.get(AppStorage.SETTINGS_LANGUAGE).then((languageStorage) => {
        const language = languageStorage ?? 'es';
        this.translate.setFallbackLang(language);
        this.translate.use(language);

        this.store.dispatch(
          appStoreSettingsActions.setLanguageAction({
            language,
          }),
        );
      });

      this.storage.get(AppStorage.SETTINGS_FORMAT).then((format) => {
        this.store.dispatch(
          appStoreSettingsActions.setFormatAction({
            format: format ?? 'units',
          }),
        );
      });

      this.storage.get(AppStorage.SETTINGS_PRECISION).then((precision) => {
        this.store.dispatch(
          appStoreSettingsActions.setPrecisionAction({
            precision: precision ?? 0,
          }),
        );
      });

      Network.getStatus().then((status) => {
        const connected = status.connected;
        this.store.dispatch(
          appStoreGlobalActions.setConnectedAction({
            connected,
          }),
        );
      });

      this.calculateDeviceScreen();

      this.checkBackgroundPermission();

      if (!this.isWeb) {
        if (this.isSmall) {
          ScreenOrientation.lock({ orientation: 'portrait' });
        } else {
          ScreenOrientation.unlock();
        }

        PushNotifications.requestPermissions().then((result) => {
          if (result.receive === 'granted') {
            // Register with Apple / Google to receive push via APNS/FCM
            PushNotifications.register();
          } else {
            // Show some error
          }
        });
      }

      this.platform.resize.subscribe(() => {
        this.calculateDeviceScreen();
      });

      this.storage.get(AppStorage.USER).then((user: SecurityUser) => {
        this.user = user;
        if (user?.collaborator) {
          this.firebaseService.subscribe(user);

          this.agentDocument = `${user.collaborator.agentCode}`;

          this.store.dispatch(appStoreGlobalActions.setUserAction({ user }));

          this.store.dispatch(
            appStoreGlobalActions.setCollaboratorAction({
              collaborator: user.collaborator,
            }),
          );

          this.store.dispatch(appStoreGlobalActions.setCompanyAction({ company: user.company }));

          this.store.dispatch(
            appStoreScreenActions.setClaimsAction({
              claims: user.claims!,
            }),
          );
        }

        if (this.sessionInStorage) {
          this.handleAppData(user);
        }

        this.storage.get(AppStorage.FORMS.APPOINTMENTS).then((appointments: any[]) => {
          this.store.dispatch(
            appStoreGlobalActions.setAppointmentsToSyncAction({
              appointmentsToSync: appointments?.length ?? 0,
            }),
          );
        });

        this.storage.get(AppStorage.FORMS.ORDERS).then((orders: any[]) => {
          this.store.dispatch(
            appStoreGlobalActions.setOrdersToSyncAction({
              ordersToSync: orders?.length ?? 0,
            }),
          );
        });

        this.storage.get(AppStorage.FORMS.CUSTOMERS).then((customers: any[]) => {
          this.store.dispatch(
            appStoreGlobalActions.setCustomersToSyncAction({
              customersToSync: customers?.length ?? 0,
            }),
          );
        });
      });

      const todayDate = new Date();
      const dateLabel = `${todayDate.getFullYear()}${todayDate.getMonth()}${todayDate.getDate()}`;
      this.storage
        .get(AppStorage.AI_CHAT_HISTORY.replace('{{DATE}}', dateLabel))
        .then((aiChatHistory: AiChatInformation[]) => {
          this.store.dispatch(
            appStoreGlobalActions.setAiChatHistoryAction({
              aiChatHistory,
            }),
          );
        });

      await Network.addListener('networkStatusChange', (status) => {
        const connected = status.connected;
        this.store.dispatch(appStoreGlobalActions.setConnectedAction({ connected }));
      });

      this.platform.pause.subscribe(() => {
        if (this.subscriptions['watchLocationFromBackground']) {
          this.subscriptions['watchLocationFromBackground'].unsubscribe();
        }
      });

      this.platform.resume.subscribe(() => {
        if (this.sessionInStorage) {
          this.whoAmI();
        }

        console.log('[DEV] Start watching again');
        this.triggerGetLocationFromBackground();
      });

      this.service.handlePositionPermission();
      this.store.select('global', 'isWatchingPosition').subscribe({
        next: (isWatchingPosition: boolean) => {
          if (isWatchingPosition) {
            Geolocation.watchPosition({ enableHighAccuracy: true, timeout: 20000, interval: 2500 }, (position) => {
              this.store.dispatch(appStoreGlobalActions.setUserPosition({ position }));
            }).then((id) => {
              this.geolocationPositionId = id;
            });
          } else if (this.geolocationPositionId) {
            Geolocation.clearWatch({ id: this.geolocationPositionId }).then();
          }
        },
      });

      this.service.cleanRestCache();
    });
  }

  toastMessageVerification = (text?: string) => {
    if (!text || text.toLowerCase() === 'undefined') {
      return '';
    }
    return text;
  };

  private readonly checkCurrentSession = () => {
    this.storage.get(AppStorage.SESSION).then((session) => {
      if (session) {
        this.sessionInStorage = session;
      }
    });
  };

  private readonly calculateDeviceScreen = () => {
    this.isWeb = this.platform.is('desktop') ?? this.platform.is('mobileweb') ?? false;
    this.isSmall =
      (this.platform.isPortrait() && this.platform.width() < 768) ||
      (this.platform.isLandscape() && this.platform.width() < 992);
    const isPortrait = this.platform.isPortrait();
    const isMedium = this.platform.width() < 992;
    const isLarge = this.platform.width() >= 992;

    this.store.dispatch(
      appStoreGlobalActions.setScreenSizeAction({
        isWeb: this.isWeb,
        isSmall: this.isSmall,
        isPortrait,
        isMedium,
        isLarge,
      }),
    );

    this.store.dispatch(
      appStoreGlobalActions.setInnerHeightAction({
        innerHeight: 0,
      }),
    );
    setTimeout(() => {
      const innerHelper: HTMLElement | null = document.querySelector('.layout-content-inner');
      const areaHelper: HTMLElement | null = document.querySelector('.main-area__helper');
      const footerHelper: HTMLElement | null = document.querySelector('.layout-footer');
      if (areaHelper && footerHelper) {
        const innerWidth = areaHelper?.clientWidth ?? 0;
        const innerHeight =
          (innerHelper?.scrollHeight ?? this.platform.height()) -
          (areaHelper?.offsetTop ?? 0) -
          (footerHelper?.scrollHeight ?? 0);

        this.store.dispatch(
          appStoreGlobalActions.setInnerHeightAction({
            innerHeight,
          }),
        );

        this.store.dispatch(
          appStoreGlobalActions.setInnerWidthAction({
            innerWidth,
          }),
        );
      }
    }, 750);
  };

  private readonly setLocalizationForPrimeNG = () => {
    const language = this.translate.getFallbackLang() ?? 'es';
    const translations = primeNgLanguage[language];
    this.primeNg.setTranslation(translations.GLOBAL_PRIMENG);
  };

  private readonly setLocalizationForDevextreme = (translation: any) => {
    const language = this.translate.currentLang() ?? localStorage.getItem('p360Language') ?? 'es';
    const messages: any = {
      ...esMessages,
      ...enMessages,
    };
    messages[language] = {
      ...messages[language],
      ...translation['GLOBAL_DEVEXTREME'],
    };
    messages.default = { ...messages[language] };

    config({
      defaultCurrency: 'USD',
    });
    locale(`${language}-US`);
    loadMessages(messages);
  };

  private readonly handleAppData = (user: SecurityUser) => {
    this.whoAmI();
    this.service.loadOfflineData(`${user.collaborator?.agentCode}`);

    this.triggerGetLocationFromBackground();
  };

  private readonly whoAmI = () => {
    this.service
      .whoAmI()
      .pipe(
        finalize(() => {
          document.querySelector('.splash-screen-wrapper')?.classList.add('hidden');
        }),
      )
      .subscribe({
        error: () => {
          this.service.loadOfflineData(this.agentDocument);
        },
      });
  };

  private readonly checkBackgroundPermission = () => {};

  private readonly triggerGetLocationFromBackground = () => {
    if (!environment.production || !this.user?.collaborator?.startLogging) {
      return;
    }

    if (this.subscriptions['watchLocationFromBackground']) {
      this.subscriptions['watchLocationFromBackground'].unsubscribe();
    }

    this.applyBackgroundCall();
    const mins = 10;
    this.subscriptions['watchLocationFromBackground'] = interval(60000 * mins).subscribe({
      next: () => {
        this.applyBackgroundCall();
      },
    });
  };

  private readonly applyBackgroundCall = () => {
    Geolocation.getCurrentPosition({ timeout: 60000 })
      .then((position) => {
        this.applyReverseLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      })
      .catch((error) => {
        console.error('Error on getting current position. ', error);
        this.savePosition({ latitude: 0, longitude: 0, address: '---' });
      });
  };

  private readonly applyReverseLocation = ({ latitude, longitude }: { latitude?: number; longitude?: number }) => {
    this.service.getReverseAddress({ lat: latitude!, lon: longitude! }).subscribe({
      next: ({ address }) => {
        this.savePosition({ latitude, longitude, address });
      },
      error: (error) => {
        console.error('Error on getting reverse location. ', error);
        this.savePosition({ latitude, longitude, address: '---' });
      },
    });
  };

  private readonly savePosition = ({
    latitude,
    longitude,
    address,
  }: {
    latitude?: number;
    longitude?: number;
    address?: string;
  }) => {
    this.service
      .saveRoute({
        agentCode: this.user?.collaborator?.agentCode,
        latitude,
        longitude,
        address,
      })
      .subscribe();
  };
}
